import { ReactNode, useState } from 'react';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import useDeleteExpense from '@/features/dashboard/expenses/hooks/useDeleteExpense.ts';
import useCancelExpense from '@/features/dashboard/expenses/hooks/useCancelExpense.ts';
import EditExpenseModal from '@/features/dashboard/expenses/components/EditExpenseModal.tsx';
import ExpenseDetailsModal from '@/features/dashboard/expenses/components/ExpenseDetailsModal.tsx';
import PayExpenseModal from '@/features/dashboard/expenses/components/PayExpenseModal.tsx';
import MatchExpenseModal from '@/features/dashboard/expenses/components/MatchExpenseModal.tsx';
import { TourExpenseListDto } from '@/features/dashboard/expenses/types/TourExpenseListDto.ts';
import { ExpensePaymentStatus } from '@/features/dashboard/expenses/types/ExpensePaymentStatus.ts';

/** Każda akcja na wydatku idzie ścieżką z `tourId`, więc target niesie oba klucze. */
interface ExpenseTarget {
  tourId: string;
  expenseId: string;
}

interface UseExpenseRowActionsParams {
  /** Tabela wyjazdu zna `tourId` z propsów, globalna wyciąga go z wiersza. */
  tourIdOf: (row: TourExpenseListDto) => string;
}

interface UseExpenseRowActionsResult {
  rowContextMenuItems: RowContextMenuItemParams<TourExpenseListDto>[];
  /** Modale menu — tabela wstawia je obok `<Table>`. */
  expenseRowModals: ReactNode;
}

const isCancelled = (row: TourExpenseListDto) =>
  row.paymentStatus === ExpensePaymentStatus.CANCELLED;

/**
 * Menu wiersza wydatku dla obu tabel. Wcześniej istniało w dwóch kopiach, które
 * zdążyły się rozjechać na przyciskach dialogu usuwania.
 */
const useExpenseRowActions = ({
  tourIdOf,
}: UseExpenseRowActionsParams): UseExpenseRowActionsResult => {
  const [detailsTarget, setDetailsTarget] = useState<ExpenseTarget | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<ExpenseTarget | null>(null);
  const [payTarget, setPayTarget] = useState<ExpenseTarget | null>(null);
  const [matchTarget, setMatchTarget] = useState<ExpenseTarget | null>(null);
  // Kasowanie i anulowanie idą mutacją związaną z wyjazdem, a dialog potwierdzenia
  // odpala się poza renderem wiersza — stąd osobny stan zamiast targetu.
  const [actionTourId, setActionTourId] = useState<string>('');

  const deleteMutation = useDeleteExpense(actionTourId);
  const cancelMutation = useCancelExpense(actionTourId);
  const { confirmDeleteWithCallback, confirmWithCallback } =
    confirmDialogUseHook();

  const target = (row: TourExpenseListDto): ExpenseTarget => ({
    tourId: tourIdOf(row),
    expenseId: row.id,
  });

  const rowContextMenuItems: RowContextMenuItemParams<TourExpenseListDto>[] = [
    {
      label: 'Szczegóły i zapłaty',
      action: (row) => setDetailsTarget(target(row)),
    },
    {
      label: 'Edytuj',
      // Anulowany wydatek nie przyjmuje już zmian kwoty ani kategorii.
      visible: (row) => !isCancelled(row),
      action: (row) => setEditTarget(target(row)),
    },
    {
      label: 'Zarejestruj zapłatę',
      visible: (row) =>
        !isCancelled(row) && row.paymentStatus !== ExpensePaymentStatus.PAID,
      action: (row) => setPayTarget(target(row)),
    },
    {
      label: 'Dopasuj do transakcji bankowej',
      visible: (row) => !isCancelled(row),
      action: (row) => setMatchTarget(target(row)),
    },
    {
      label: 'Anuluj wydatek',
      visible: (row) => !isCancelled(row),
      action: (row) => {
        setActionTourId(tourIdOf(row));
        confirmWithCallback({
          title: 'Anuluj wydatek',
          description:
            'Wpięte zapłaty zostaną odpięte, a transakcje wrócą do puli do rozdysponowania. Anulowany wydatek nie przyjmuje już zapłat.',
          confirmText: 'Anuluj wydatek',
          cancelText: 'Wróć',
          onConfirm: () => cancelMutation.mutate(row.id),
        });
      },
    },
    {
      label: 'Usuń',
      variant: 'destructive',
      // Backend odbija kasowanie wydatku z alokacjami (`ExpenseException.hasAllocations`),
      // więc pozycja gaśnie zamiast czekać na błąd z serwera.
      disabled: (row) => row.paidAmount > 0,
      disabledTooltip: () =>
        'Wydatku z zapłatami nie da się usunąć — użyj „Anuluj wydatek”',
      action: (row) => {
        setActionTourId(tourIdOf(row));
        confirmDeleteWithCallback({
          title: 'Usuń wydatek',
          description:
            'Czy na pewno chcesz usunąć ten wydatek? Tej operacji nie da się cofnąć.',
          confirmText: 'Usuń',
          cancelText: 'Wróć',
          onConfirm: () => deleteMutation.mutate(row.id),
        });
      },
    },
  ];

  const expenseRowModals = (
    <>
      {detailsTarget && (
        <ExpenseDetailsModal
          tourId={detailsTarget.tourId}
          expenseId={detailsTarget.expenseId}
          onClose={() => setDetailsTarget(null)}
        />
      )}

      {editTarget && (
        <EditExpenseModal
          tourId={editTarget.tourId}
          expenseId={editTarget.expenseId}
          onClose={() => setEditTarget(null)}
        />
      )}

      {payTarget && (
        <PayExpenseModal
          tourId={payTarget.tourId}
          expenseId={payTarget.expenseId}
          onClose={() => setPayTarget(null)}
        />
      )}

      {matchTarget && (
        <MatchExpenseModal
          tourId={matchTarget.tourId}
          expenseId={matchTarget.expenseId}
          onClose={() => setMatchTarget(null)}
        />
      )}
    </>
  );

  return { rowContextMenuItems, expenseRowModals };
};

export default useExpenseRowActions;
